import { pushPurchaseEvent } from '../util/gtmUtils';

const isBrowser = typeof window !== 'undefined';

export const trackPlausibleEvent = (eventName, options = {}) => {
  try {
    if (isBrowser && typeof window.plausible === 'function') {
      window.plausible(eventName, options);
    }
  } catch (_) {}
};

export const trackPurchase = ({
  listing,
  deliveryMethod,
  paymentMethod,
  quantity = 1,
  shippingPrice = 0,
  paidTotal,
}) => {
  const price = listing?.attributes?.price;
  const currency = paidTotal?.currency || price?.currency;
  // shippingPrice arrives in minor units, as orderParams stores it.
  const shipping =
    deliveryMethod === 'shipping' ? shippingPrice / 100 || 0 : 0;
  // Mirrors the value sent to GTM: what the buyer paid (the transaction's
  // payinTotal, which includes shipping and any voucher discount). Falls back
  // to line total plus shipping.
  const amount =
    typeof paidTotal?.amount === 'number'
      ? paidTotal.amount / 100
      : price
      ? (price.amount / 100) * (quantity || 1) + shipping
      : undefined;
  const title = listing?.attributes?.title;
  const listingId = listing?.id?.uuid;
  const slug = title
    ? title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    : undefined;

  trackPlausibleEvent('Purchase', {
    ...(amount && currency ? { revenue: { currency, amount } } : {}),
    props: {
      ...(title ? { listing_title: title } : {}),
      ...(listingId ? { listing_id: listingId } : {}),
      ...(slug ? { slug } : {}),
      ...(deliveryMethod ? { delivery_method: deliveryMethod } : {}),
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
    },
  });
};

const KLARNA_TRACKING_KEY = 'klarnaCheckoutTracking';

const slugify = title =>
  title ? title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : undefined;

/**
 * Klarna payments finish on the payment return page after a redirect, so the
 * purchase data is kept in sessionStorage until then. Amounts are minor units.
 */
export const storeKlarnaTrackingData = ({
  listing,
  deliveryMethod,
  quantity = 1,
  shippingPrice = 0,
  paidTotal,
}) => {
  if (!isBrowser) return;
  try {
    const publicData = listing?.attributes?.publicData || {};
    sessionStorage.setItem(
      KLARNA_TRACKING_KEY,
      JSON.stringify({
        listingTitle: listing?.attributes?.title,
        listingId: listing?.id?.uuid,
        brand: publicData.brand,
        designer: publicData.designer,
        priceAmount: listing?.attributes?.price?.amount,
        priceCurrency: listing?.attributes?.price?.currency,
        quantity: quantity || 1,
        shippingAmount: deliveryMethod === 'shipping' ? shippingPrice || 0 : 0,
        paidAmount: typeof paidTotal?.amount === 'number' ? paidTotal.amount : undefined,
        paidCurrency: paidTotal?.currency,
        deliveryMethod,
      })
    );
  } catch (_) {}
};

/**
 * Report a completed Klarna purchase to Plausible and GTM, once.
 *
 * @param {string} transactionId the Sharetribe transaction id
 */
export const fireKlarnaPurchaseEvent = transactionId => {
  if (!isBrowser) return;
  try {
    const raw = sessionStorage.getItem(KLARNA_TRACKING_KEY);
    if (!raw) return;
    sessionStorage.removeItem(KLARNA_TRACKING_KEY);
    const data = JSON.parse(raw);
    const {
      listingTitle,
      listingId,
      priceAmount,
      priceCurrency,
      deliveryMethod,
      quantity = 1,
      shippingAmount = 0,
    } = data;

    const currency = data.paidCurrency || priceCurrency;
    const totalMinor =
      typeof data.paidAmount === 'number'
        ? data.paidAmount
        : typeof priceAmount === 'number'
        ? priceAmount * quantity + shippingAmount
        : undefined;
    const amount = typeof totalMinor === 'number' ? totalMinor / 100 : undefined;

    trackPlausibleEvent('Purchase', {
      ...(amount && currency ? { revenue: { currency, amount } } : {}),
      props: {
        ...(listingTitle ? { listing_title: listingTitle } : {}),
        ...(listingId ? { listing_id: listingId } : {}),
        ...(listingTitle ? { slug: slugify(listingTitle) } : {}),
        ...(deliveryMethod ? { delivery_method: deliveryMethod } : {}),
        payment_method: 'klarna',
      },
    });

    if (typeof amount === 'number' && currency) {
      const price = typeof priceAmount === 'number' ? priceAmount / 100 : 0;
      pushPurchaseEvent({
        transactionId,
        value: amount,
        currency,
        shipping: shippingAmount / 100,
        items: [
          {
            item_id: listingId,
            item_name: listingTitle || 'Unknown Product',
            price,
            quantity,
            currency,
            item_brand: data.brand || '',
            item_designer: data.designer || '',
          },
        ],
      });
    }
  } catch (_) {}
};
